type ColorShade =
  | "50"
  | "100"
  | "200"
  | "300"
  | "350"
  | "400"
  | "500"
  | "600"
  | "700"
  | "800"
  | "850"
  | "900"
  | "950";
export type Colors = Record<ColorShade, string>;

export default {
  borderRadius: {
    md: "0.375rem",
    xl: "0.75rem",
    full: "9999px",
  },

  sidebarWidth: "15rem" /* 240px */,
  contentAreaWidth: "44.688rem" /* 715px */,
  ccfContentAreaHeight: "48.125rem" /* 770px */,
  mhContentAreaHeight: "29rem" /* 464px - medical history */,
  testingContentAreaHeight: "35.5rem" /* 568px */,
  targetFullWidth: "59.688rem" /* 955px */,

  colors: {
    warning: "#F2994A",
    concern: "#D9534F",
    concernText: "#FCA082",
    innerContainer: "hsla(222, 17%, 15%, 0.2)",
    outerContainer: "#2A2F3C",
    containerWithBackgroundDark: "#188c9a",
    containerSelectedDark: "#283845",
    lightBorder: "#ced4da",
    skeletonLight: "rgba(54, 60, 73, 0.15)",
    skeletonDark: "rgba(54, 60, 73, 0.75)",
    skeletonBase: "#3d4452", // Base skeleton color (slightly lighter than dark backgrounds)
    skeletonShimmer: "#4d5565", // Shimmer highlight color (lighter for visible animation)
    goodProgress: "#75BF44",
    navDefaultColor: "#919596",
    navDefaultHoverColor: "#f2994a",

    blackAlpha: [
      "rgba(0, 0, 0, 0.04)", // [0] blackAlpha.0
      "rgba(0, 0, 0, 0.06)", // [1] blackAlpha.1
      "rgba(0, 0, 0, 0.08)", // [2] blackAlpha.2
      "rgba(0, 0, 0, 0.16)", // [3] blackAlpha.3
      "rgba(0, 0, 0, 0.24)", // [4] blackAlpha.4
      "rgba(0, 0, 0, 0.36)", // [5] blackAlpha.5
      "rgba(0, 0, 0, 0.48)", // [6] blackAlpha.6
      "rgba(0, 0, 0, 0.64)", // [7] blackAlpha.7
      "rgba(0, 0, 0, 0.80)", // [8] blackAlpha.8
      "rgba(0, 0, 0, 0.92)", // [9] blackAlpha.9
    ],

    bokara: Array.from({ length: 10 }, () => "#131211"),

    blue: {
      100: "#188c9a",
      200: "#99A0B1",
      500: "#20242d",
    },

    blueChill: [
      "#ddfdff", // [0] blueChill.0
      "#b7f0f7", // [1] blueChill.1
      "#8ee4ef", // [2] blueChill.2
      "#65d9e7", // [3] blueChill.3
      "#3ecedf", // [4] blueChill.4
      "#27b5c6", // [5] blueChill.5
      "#188c9a", // [6] blueChill.6
      "#09646f", // [7] blueChill.7
      "#003d44", // [8] blueChill.8
      "#00161a", // [9] blueChill.9
    ],

    error: "#D9534F",

    charade: [
      "#f6f7f9", // [0] charade.0
      "#eceef2", // [1] charade.1
      "#d4d9e3", // [2] charade.2
      "#afb7ca", // [3] charade.3
      "#8391ad", // [4] charade.4
      "#8a9bb0", // [5] charade.5
      "#4f5c7a", // [6] charade.6
      "#414b63", // [7] charade.7
      "#384054", // [8] charade.8
      "#2a2f3c", // [9] charade.9
    ],

    chestnutRose: {
      "50": "#fdf3f3",
      "100": "#fbe6e5",
      "200": "#f8d1d0",
      "300": "#f2b1af",
      "400": "#e88481",
      "500": "#d9534f",
      "600": "#c73f3b",
      "700": "#a7322e",
      "800": "#8a2d2a",
      "900": "#742a28",
      "950": "#3e1211",
    } as Colors,

    fuscousGray: {
      "50": "#f6f6f6",
      "100": "#e7e7e7",
      "200": "#d1d1d1",
      "300": "#b0b0b0",
      "350": "#99A0B1",
      "400": "#888888",
      "500": "#6d6d6d",
      "600": "#5d5d5d",
      "700": "#4d4d4d",
      "800": "#454545",
      "900": "#3d3d3d",
      "950": "#262626",
    } as Colors,

    grass: {
      "50": "#ffffdf",
      "100": "#e6ffc6",
      "200": "#ccffac",
      "300": "#a8d377",
      "400": "#b2eb92",
      "500": "#99d279",
      "600": "#80b860",
      "700": "#669f46",
      "800": "#4c852c",
      "900": "#336c13",
      "950": "#195200",
    } as Colors,

    gray: {
      // 50:
      100: "#E5E5E5",
      200: "#bdbdbd",
      300: "#B5BBC7",
      400: "#929597",
      500: "#5c5d61",
      600: "#333a47",
      700: "#363C49",
      800: "#626B81",
      900: "#131211",
      // 950:
    },

    green: {
      500: "#559232",
    },

    lima: [
      "#f1faeb", // [0] lima.0
      "#e2f2d5", // [1] lima.1
      "#c7e7af", // [2] lima.2
      "#a3d680", // [3] lima.3
      "#81c457", // [4] lima.4
      "#6ab63e", // [5] lima.5
      "#4a862a", // [6] lima.6
      "#3a6724", // [7] lima.7
      "#325222", // [8] lima.8
      "#2c4720", // [9] lima.9
    ],

    roman: {
      "50": "#fef2f2",
      "100": "#fde3e3",
      "200": "#fccccc",
      "300": "#f9a8a8",
      "400": "#f47575",
      "500": "#eb5757",
      "600": "#d62c2c",
      "700": "#b42121",
      "800": "#951f1f",
      "900": "#7c2020",
      "950": "#430c0c",
    } as Colors,

    sandyBrown: {
      "50": "#fef7ee",
      "100": "#fdeed7",
      "200": "#fad9ae",
      "300": "#f6bc7b",
      "400": "#f2994a",
      "500": "#ee7921",
      "600": "#df5f17",
      "700": "#b94815",
      "800": "#933919",
      "900": "#773217",
      "950": "#40170a",
    } as Colors,

    shark: [
      "#eef1f9", // [0] shark.0
      "#d2d6df", // [1] shark.1
      "#b5bbc7", // [2] shark.2
      "#99a0b1", // [3] shark.3
      "#7c859b", // [4] shark.4
      "#626b81", // [5] shark.5
      "#4c5365", // [6] shark.6
      "#363c49", // [7] shark.7
      "#20242d", // [8] shark.8
      "#090c14", // [9] shark.9
    ],

    red: {
      "50": "#fa5252",
      // Theme provider requires 10 shades
      "100": "#fa5252",
      "200": "#fa5252",
      "300": "#fa5252",
      "400": "#fa5252",
      "500": "#fa5252",
      "600": "#fa5252",
      "700": "#fa5252",
      "800": "#fa5252",
      "900": "#fa5252",
    } as Colors,

    whiteAlpha: [
      "rgba(255, 255, 255, 0.04)", // [0] whiteAlpha.0
      "rgba(255, 255, 255, 0.06)", // [1] whiteAlpha.1
      "rgba(255, 255, 255, 0.08)", // [2] whiteAlpha.2
      "rgba(255, 255, 255, 0.16)", // [3] whiteAlpha.3
      "rgba(255, 255, 255, 0.24)", // [4] whiteAlpha.4
      "rgba(255, 255, 255, 0.36)", // [5] whiteAlpha.5
      "rgba(255, 255, 255, 0.48)", // [6] whiteAlpha.6
      "rgba(255, 255, 255, 0.64)", // [7] whiteAlpha.7
      "rgba(255, 255, 255, 0.80)", // [8] whiteAlpha.8
      "rgba(255, 255, 255, 0.92)", // [9] whiteAlpha.9
    ],

    transparent: "transparent",
    black: "#000000",
    white: "#ffffff",
    offWhite: "#EEF1F9",

    // Slate palette — used by Clinic Management and other light-theme pages
    // Maps directly to Tailwind's slate scale from the design mockups
    slate: [
      "#f8fafc", // [0] slate.0 — page background
      "#f1f5f9", // [1] slate.1 — subtle section bg
      "#e2e8f0", // [2] slate.2 — borders
      "#cbd5e1", // [3] slate.3 — inactive/disabled borders
      "#94a3b8", // [4] slate.4 — muted text / icons
      "#64748b", // [5] slate.5 — secondary text
      "#475569", // [6] slate.6 — moderate text
      "#334155", // [7] slate.7 — emphasis text
      "#1e293b", // [8] slate.8 — primary headings
      "#0f172a", // [9] slate.9 — max contrast
    ],

    // Disruption scope accent colors — maps to mockup scope card palette
    disruption: {
      temporary: { bg: "#fffbeb", border: "#d97706", text: "#d97706", iconColor: "#f59e0b" }, // amber
      serviceSpecific: { bg: "#eff6ff", border: "#2563eb", text: "#2563eb", iconColor: "#3b82f6" }, // blue
      permanent: { bg: "#fef2f2", border: "#dc2626", text: "#dc2626", iconColor: "#ef4444" }, // red
    },
    outcomeBackground: "#303543",
    headerBorderColor: "#188C9A",
    switchActiveColor: "#188C9A",
    switchThumbColor: "#20242D",
    modalBackground: "#20242D",
    // Dedicated page-level background for dark admin detail pages (clinic, etc.)
    // Sits between shark[8] (#20242d) and shark[9] (#090c14) — dark enough to
    // contrast clearly against charade[9] (#2a2f3c) card panels.
    adminDetailPageBg: "#14171e",

    // ── CerteDrive design-system tokens (canonical, dark-mode) ──────────────
    // Source: certedrive-design-system.html / clinic-management-va.html
    // These are the authoritative values; use these in all new admin modules.
    dsPageBg: "#0f1318", // --bg          outermost surface
    dsPanelOuter: "#1c2330", // --panel-outer panel card bg
    dsPanelInner: "#222d3d", // --panel-inner inner area bg
    dsPanelRow: "#1e2736", // --panel-row   table row bg
    dsPanelBanner: "#253044", // --panel-banner section header bg
    dsBorder: "rgba(255,255,255,0.07)", // --border
    dsBorderMid: "rgba(255,255,255,0.11)", // --border-mid
    dsBorderStrong: "rgba(255,255,255,0.16)", // --border-strong
    dsText: "#dde5ef", // --text        primary text
    dsTextMuted: "#9ab0c8", // --text-muted  secondary text
    dsTextDim: "#6e8298", // --text-dim    tertiary/inactive
    dsAccent: "#3ECEDF", // --accent      cyan workflow
    dsAccentDim: "rgba(62,206,223,0.12)", // --accent-dim
    dsAccentDeep: "#1A9EAE", // --accent-deep
    drugScreenTabColor: "#9370DB",
    drugScreenTabHover: "#3D3D3D",

    // Driver manager error card colors
    lavender: {
      light: "#D4C4F0", // Light purple for avatar
      base: "#B099E6", // Purple for border/avatar
      dark: "#8B6CCF", // Darker purple
    },
    amber: {
      light: "#F7CC86", // Yellow/amber for warning text
      base: "#F2994A", // Orange
    },
  },

  fontFamily: {
    inter: "'Inter var', 'Inter', sans-serif",
    interVar: "'Inter var', sans-serif",
    montserrat: "'Montserrat', sans-serif",
    openSans: "Open Sans, sans-serif",
  },

  fontSize: {
    xs2: "0.625rem" /* 10px */,
    xs1: "0.688rem" /* 11px */,
    xs: "0.75rem" /* 12px */,
    sm1: "0.8125rem" /* 13px */,
    sm: "0.875rem" /* 14px */,
    sm2: "0.9375rem" /* 15px */,
    base: "1rem" /* 16px */,
    lg: "1.125rem" /* 18px */,
    xl: "1.25rem" /* 20px */,
    xl2: "1.5rem" /* 24px */,
    xl3: "1.875rem" /* 30px */,
  },

  fontWeight: {
    thin: 100,
    extraLight: 200,
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
  },

  spacing: {
    px: "1px", // 0.0625rem;
    full: "100%", // 100%
    0: "0",
    0.5: "0.125rem" /* 2px */,
    0.75: "0.1875rem" /* 3px */,
    1: "0.25rem" /* 4px */,
    1.25: "0.3125rem" /* 5px */,
    1.5: "0.375rem" /* 6px */,
    1.75: "0.4375rem" /* 7px */,
    2: "0.5rem" /* 8px */,
    2.125: "0.53125rem" /* 8.5px */,
    2.25: "0.5625rem" /* 9px */,
    2.4: "0.6rem" /* 9.6px */,
    2.5: "0.625rem" /* 10px */,
    3: "0.75rem" /* 12px */,
    3.5: "0.875rem" /* 14px */,
    3.75: "0.9375rem" /* 15px */,
    4: "1rem" /* 16px */,
    4.4: "1.1rem" /* 17.6px */,
    4.8: "1.2rem" /* 19.2px */,
    5: "1.25rem" /* 20px */,
    5.25: "1.3rem" /* 22px */,
    5.5: "1.375rem" /* 22px */,
    6: "1.5rem" /* 24px */,
    7: "1.75rem" /* 28px */,
    7.5: "1.875rem" /* 30px */,
    8: "2rem" /* 32px */,
    9: "2.25rem" /* 36px */,
    10: "2.5rem" /* 40px */,
    11: "2.75rem" /* 44px */,
    11.5: "2.875rem" /* 46px */,
    12: "3rem" /* 48px */,
    14: "3.5rem" /* 56px */,
    15: "3.75rem" /* 60px */,
    16: "4rem" /* 64px */,
    17: "4.25rem",
    18: "4.5rem",
    20: "5rem" /* 80px */,
    24: "6rem" /* 96px */,
    28: "7rem" /* 112px */,
    32: "8rem" /* 128px */,
    34: "8.5rem" /* 136px */,
    36: "9rem" /* 144px */,
    40: "10rem" /* 160px */,
    44: "11rem" /* 176px */,
    48: "12rem" /* 192px */,
    52: "13rem" /* 208px */,
    56: "14rem" /* 224px */,
    60: "15rem" /* 240px */,
    64: "16rem" /* 256px */,
    72: "18rem" /* 288px */,
    72.5: "18.5rem" /* 296px */,
    80: "20rem" /* 320px */,
    96: "24rem" /* 384px */,
    100: "25rem" /* 400px */,
    120: "30rem" /* 480px */,
    125: "31.25rem" /* 500px */,
  },
};
